import type { ComponentType, ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  Crown,
  Eye,
  Film,
  Search,
  Sparkles,
  Star,
  Ticket,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useNowShowing } from "@/hooks/useNowShowing";
import { VIP_PERKS } from "@/config/vipPerks";

const BulbFrame = ({ dense = false }: { dense?: boolean }) => (
  <div aria-hidden className="pointer-events-none absolute inset-0">
    <div className="absolute left-4 right-4 top-4 flex justify-between">
      {Array.from({ length: dense ? 38 : 24 }).map((_, i) => <span key={`t-${i}`} className="marquee-bulb" style={{ animationDelay: `${(i % 9) * 0.18}s` }} />)}
    </div>
    <div className="absolute bottom-4 left-4 right-4 flex justify-between">
      {Array.from({ length: dense ? 38 : 24 }).map((_, i) => <span key={`b-${i}`} className="marquee-bulb" style={{ animationDelay: `${((i + 4) % 9) * 0.18}s` }} />)}
    </div>
    <div className="absolute bottom-10 left-4 top-10 hidden flex-col justify-between sm:flex">
      {Array.from({ length: dense ? 16 : 10 }).map((_, i) => <span key={`l-${i}`} className="marquee-bulb" style={{ animationDelay: `${((i + 2) % 9) * 0.18}s` }} />)}
    </div>
    <div className="absolute bottom-10 right-4 top-10 hidden flex-col justify-between sm:flex">
      {Array.from({ length: dense ? 16 : 10 }).map((_, i) => <span key={`r-${i}`} className="marquee-bulb" style={{ animationDelay: `${((i + 6) % 9) * 0.18}s` }} />)}
    </div>
  </div>
);

const GoldButton = ({ children, to = "/auth", className = "" }: { children: ReactNode; to?: string; className?: string }) => (
  <Link to={to}>
    <Button
      size="lg"
      className={cn(
        // Admit-one ticket stub — brass/marquee gold, matching the theatre's bulbs
        "h-12 rounded-md px-7 font-black uppercase tracking-[0.18em] text-accent-foreground",
        "bg-gradient-to-r from-[hsl(45_100%_62%)] via-[hsl(35_96%_56%)] to-[hsl(25_95%_55%)] bg-[length:200%_100%] animate-smooth-gradient",
        "shadow-lg shadow-marquee-bulb/30 hover:shadow-[0_0_38px_hsl(45_100%_60%/0.5)]",
        className
      )}
    >
      {children}
    </Button>
  </Link>
);

/** Ghost "look around" door — browsing needs no account. */
const BrowseButton = ({ className = "" }: { className?: string }) => (
  <Link
    to="/?browse=1"
    className={cn(
      "inline-flex h-12 items-center gap-2 rounded-md border border-marquee-bulb/35 bg-background/40 px-6 text-sm font-bold uppercase tracking-[0.16em] text-foreground/80 transition hover:border-marquee-bulb hover:text-marquee-bulb",
      className
    )}
  >
    <Eye className="h-4 w-4" /> Look around first
  </Link>
);

const TheatreHero = () => {
  const { titles } = useNowShowing(12);

  return (
    <section className="relative min-h-[calc(100vh-88px)] overflow-hidden rounded-b-[2rem] border-x border-b border-marquee-bulb/25 bg-velvet film-grain">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_36%,hsl(var(--marquee-bulb)/0.22),transparent_32%),linear-gradient(90deg,hsl(356_58%_16%/0.9),transparent_23%,transparent_77%,hsl(356_58%_16%/0.9))]" />
      <div className="spotlight-beam opacity-80" />
      <BulbFrame dense />

      {/* Curtains part once, then get out of the way entirely (mobile text safety) */}
      <motion.div
        aria-hidden
        initial={{ x: 0 }}
        animate={{ x: "-100%" }}
        transition={{ duration: 2.4, ease: [0.65, 0, 0.35, 1] }}
        className="pointer-events-none absolute inset-y-0 left-0 z-0 w-[52%] bg-[repeating-linear-gradient(90deg,hsl(356_64%_18%)_0px,hsl(356_68%_10%)_28px,hsl(356_58%_22%)_56px)] shadow-[inset_-48px_0_80px_hsl(0_0%_0%/0.65)]"
      />
      <motion.div
        aria-hidden
        initial={{ x: 0 }}
        animate={{ x: "100%" }}
        transition={{ duration: 2.4, ease: [0.65, 0, 0.35, 1] }}
        className="pointer-events-none absolute inset-y-0 right-0 z-0 w-[52%] bg-[repeating-linear-gradient(90deg,hsl(356_58%_22%)_0px,hsl(356_68%_10%)_28px,hsl(356_64%_18%)_56px)] shadow-[inset_48px_0_80px_hsl(0_0%_0%/0.65)]"
      />

      <div className="relative z-20 mx-auto flex min-h-[calc(100vh-88px)] max-w-6xl flex-col items-center justify-center px-4 pt-24 pb-32 text-center sm:py-24">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }} className="space-y-6 sm:space-y-8">
          <div className="mx-auto w-fit rounded-md border border-marquee-bulb/40 bg-background/70 px-3 py-2 text-[9px] font-black uppercase tracking-[0.28em] text-marquee-bulb shadow-[0_0_28px_hsl(var(--marquee-bulb)/0.18)] sm:px-4 sm:text-[10px] sm:tracking-[0.34em]">
            Swiftix Theatre · Doors open
          </div>
          <h1 className="cinema-headline mx-auto max-w-5xl break-words text-[2.5rem] leading-[1.05] sm:text-8xl lg:text-9xl">
            Tonight starts at <span className="text-marquee-bulb marquee-glow">your seat.</span>
          </h1>
          <p className="mx-auto max-w-2xl text-sm leading-relaxed text-foreground/70 sm:text-lg">
            Walk the whole theatre without an account. Claim a free ticket when you want to search,
            save titles, host the lobby vote, or build a marathon.
          </p>
          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <GoldButton>
              <Ticket className="h-4 w-4" /> Claim your free ticket
            </GoldButton>
            <BrowseButton />
          </div>
        </motion.div>
      </div>

      {/* Live "now showing" ticker — real TMDB trending titles, never a fixed list */}
      {titles.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 z-20 overflow-hidden border-y border-marquee-bulb/20 bg-background/80 py-3 backdrop-blur">
          <motion.div className="flex w-max gap-10" animate={{ x: [0, -520] }} transition={{ duration: 42, repeat: Infinity, ease: "linear" }}>
            {[...titles, ...titles, ...titles].map((movie, i) => (
              <span key={`${movie.id}-${i}`} className="cinema-eyebrow whitespace-nowrap text-marquee-bulb">
                Now trending · {movie.title}{movie.year ? ` · ${movie.year}` : ""}
              </span>
            ))}
          </motion.div>
        </div>
      )}
    </section>
  );
};

/** Real posters from TMDB trending — the first honest taste of browsing. */
const NowShowingBalcony = () => {
  const { titles, loading } = useNowShowing(12);

  return (
    <section id="now-showing" className="relative mt-14 px-3 sm:mt-20 sm:px-4">
      <div className="mx-auto max-w-6xl rounded-xl border border-marquee-bulb/25 bg-background p-4 shadow-2xl backdrop-blur sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 px-1">
          <div>
            <p className="cinema-eyebrow text-[10px] tracking-[0.26em] text-marquee-bulb sm:text-xs">On the marquee right now</p>
            <h2 className="text-lg font-black sm:text-2xl">Trending this week</h2>
          </div>
          <Link to="/?browse=1" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.16em] text-foreground/60 transition hover:text-marquee-bulb">
            Browse it all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 md:grid-cols-6">
          {(loading ? Array.from({ length: 12 }) : titles).map((movie, index) => {
            const title = movie as { id?: number; title?: string; poster?: string | null; year?: number | null; rating?: number } | undefined;
            return (
              <motion.div
                key={title?.id ?? `skeleton-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: Math.min(index * 0.03, 0.3) }}
                className="group relative aspect-[2/3] overflow-hidden rounded-md bg-muted/80"
              >
                {title?.poster && (
                  <img src={title.poster} alt={title.title ?? ""} loading="lazy" decoding="async" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                )}
                {title?.title && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background/95 to-transparent p-2.5 opacity-0 transition group-hover:opacity-100 sm:p-3">
                    <p className="truncate text-sm font-black">{title.title}</p>
                    <p className="flex items-center gap-1 text-xs text-marquee-bulb">
                      <Star className="h-3 w-3 fill-current" />
                      {(title.rating ?? 0).toFixed(1)}{title.year ? ` · ${title.year}` : ""}
                    </p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

type Room = {
  icon: ComponentType<{ className?: string }>;
  door: string;
  room: string;
  title: string;
  line: string;
};

const ROOMS: Room[] = [
  { icon: Search, door: "01", room: "Discovery", title: "Quick Discovery", line: "Choose a mood, genre, or kind of night and find your next screening." },
  { icon: Sparkles, door: "02", room: "The Vault", title: "Hidden gems", line: "Curated corners of the catalogue for the nights when nothing obvious appeals." },
  { icon: Film, door: "03", room: "The Archive", title: "Watchlist", line: "Saved, Watching, Watched — your own record of every screening." },
  { icon: Users, door: "04", room: "The Lobby", title: "Movie Night", line: "Invite friends by handle, vote live, and settle what plays tonight." },
  { icon: CalendarClock, door: "05", room: "The Program", title: "Watch Planner", line: "Lay a marathon out night by night. One active program is free." },
  { icon: Crown, door: "06", room: "The Balcony", title: "Swiftix VIP", line: "Advanced filters, title analysis, exports, and your Year in Review." },
];

/** Honest map of the house: every room is walkable, none of them act without a ticket. */
const HouseMap = () => (
  <section id="house" className="mx-auto max-w-6xl space-y-8 px-3 py-16 sm:px-4 sm:py-24">
    <div className="mx-auto max-w-3xl text-center">
      <p className="cinema-eyebrow text-marquee-bulb">The map of the house</p>
      <h2 className="cinema-headline mt-3 text-4xl sm:text-6xl">Six rooms. Walk them all.</h2>
      <p className="mt-4 text-foreground/65">
        Every door is unlocked for a look around. Taking a seat — searching, saving, voting,
        planning — is what the free ticket is for.
      </p>
    </div>
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {ROOMS.map(({ icon: Icon, door, room, title, line }) => (
        <div
          key={title}
          className="group relative overflow-hidden rounded-t-[1.6rem] rounded-b-lg border border-marquee-bulb/25 bg-gradient-to-b from-[hsl(356_58%_14%/0.55)] to-card/40 p-5 pt-9 transition hover:border-marquee-bulb/60"
        >
          {/* Door plate above the arch */}
          <span className="absolute left-1/2 top-2.5 -translate-x-1/2 rounded-sm border border-marquee-bulb/40 bg-background/80 px-2 py-0.5 text-[9px] font-black tracking-[0.24em] text-marquee-bulb">
            DOOR {door}
          </span>
          {/* Warm light spilling under the door */}
          <div className="pointer-events-none absolute inset-x-6 bottom-0 h-10 bg-[radial-gradient(ellipse_at_bottom,hsl(var(--marquee-bulb)/0.28),transparent_70%)] opacity-0 transition group-hover:opacity-100" />

          <div className="mb-4 flex items-center justify-between">
            <span className="cinema-eyebrow text-marquee-bulb">{room}</span>
            <span className="grid h-9 w-9 place-items-center rounded-full border border-marquee-bulb/30 bg-marquee-bulb/10 text-marquee-bulb transition group-hover:bg-marquee-bulb/20">
              <Icon className="h-4 w-4" />
            </span>
          </div>
          <h3 className="text-xl font-black">{title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground/60">{line}</p>
          <div className="mt-5 flex items-center justify-between border-t border-dashed border-marquee-bulb/25 pt-3">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-foreground/50">
              <Eye className="h-3 w-3" /> Open to walk through
            </span>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-marquee-bulb">
              <Ticket className="h-3 w-3" /> Ticket to use
            </span>
          </div>
        </div>
      ))}
    </div>
  </section>
);


const BalconyTicket = () => (
  <section className="mx-auto max-w-5xl px-3 py-16 sm:px-4 sm:py-24">
    <div className="vip-ticket overflow-hidden p-6 sm:p-8">
      <div className="grid gap-8 md:grid-cols-[1.2fr_0.8fr] md:items-center">
        <div>
          <p className="cinema-eyebrow text-marquee-bulb">Swiftix VIP · Balcony ticket</p>
          <h2 className="cinema-headline mt-3 text-4xl sm:text-6xl">For moviegoers who live here.</h2>
          <p className="mt-4 max-w-2xl text-foreground/70">The free theatre is generous. VIP is the balcony: deeper analysis, unlimited plans, richer discovery, and your year in film.</p>
          <div className="mt-6 flex flex-wrap items-end gap-3"><span className="text-5xl font-black text-marquee-bulb">$3.75</span><span className="pb-2 text-foreground/60">/mo equivalent · $45/year</span></div>
          <GoldButton to="/vip" className="mt-6"><Crown className="h-4 w-4" /> View the full VIP programme</GoldButton>
        </div>
        <div className="space-y-2 border-t border-dashed border-marquee-bulb/25 pt-6 md:border-l md:border-t-0 md:pl-8 md:pt-0">
          {(['discovery', 'insights', 'planning', 'tools'] as const).map((area) => {
            const count = VIP_PERKS.filter((perk) => perk.area === area).length;
            const labels = { discovery: 'Discovery', insights: 'Insights', planning: 'Planning & social', tools: 'Tools & identity' };
            return <div key={area} className="flex items-start gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-marquee-bulb" /><span><strong>{labels[area]}</strong> · {count} VIP benefits</span></div>;
          })}
        </div>
      </div>
      <div className="mt-8 flex items-end justify-between border-t border-dashed border-marquee-bulb/25 pt-4 text-[10px] uppercase tracking-[0.28em] text-foreground/40">
        <span>Admit · One</span><div className="hidden h-8 items-end gap-[2px] sm:flex">{Array.from({ length: 50 }).map((_, i) => <span key={i} className="w-[2px] bg-foreground/50" style={{ height: `${28 + ((i * 29) % 70)}%` }} />)}</div><span>Seat · VIP</span>
      </div>
    </div>
  </section>
);

const FinalCall = () => (
  <section className="px-3 pb-20 text-center sm:px-4">
    <div className="mx-auto max-w-3xl rounded-xl border border-marquee-bulb/25 bg-velvet p-8 shadow-[0_0_80px_hsl(var(--marquee-bulb)/0.12)] sm:p-12">
      <p className="cinema-eyebrow text-marquee-bulb">House lights are dimming</p>
      <h2 className="cinema-headline mt-3 text-4xl sm:text-6xl">Come in. The seat is free.</h2>
      <p className="mx-auto mt-4 max-w-xl text-foreground/65">No credit card. No ads. Wander the theatre as long as you like, and take a seat when you want it to remember you.</p>
      <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <GoldButton><Ticket className="h-4 w-4" /> Take your seat</GoldButton>
        <BrowseButton />
      </div>
    </div>
  </section>
);

export const GuestShowcase = () => (
  <div className="bg-background">
    <TheatreHero />
    <NowShowingBalcony />
    <HouseMap />
    <BalconyTicket />
    <FinalCall />
  </div>
);

export default GuestShowcase;
